import { push } from "connected-react-router";
import { getActiveLanguage } from "react-localize-redux";
import { startSubmit } from "redux-form";
import jwtDecode from "jwt-decode";
import { showMessageModal } from "./Modal";
import apiClient from "../../helpers/apiClient";
import { createLoadingSelector } from "./Loading";
import {
  setCookie,
  deleteCookie,
  getCookie,
  stringToBoolean
} from "../../helpers";
import {
  JWT_TOKEN,
  NO_OF_TRIES,
  VERIFY_GOLD_CARD_ADDRESS
} from "../../constants";
import { getToken } from "./XsrfToken";
import { formId as signUpFormId } from "../../views/modules/SignUpForm";

export const LOGIN = "LOGIN";
export const LOGIN_REQUEST = "LOGIN_REQUEST";
export const LOGIN_SUCCESS = "LOGIN_SUCCESS";
export const LOGIN_FAILURE = "LOGIN_FAILURE";

export const LOGOUT = "LOGOUT";
export const LOGOUT_REQUEST = "LOGOUT_REQUEST";
export const LOGOUT_SUCCESS = "LOGOUT_SUCCESS";
export const LOGOUT_FAILURE = "LOGOUT_FAILURE";

export const REFRESH_TOKEN = "REFRESH_TOKEN";
export const REFRESH_TOKEN_REQUEST = "REFRESH_TOKEN_REQUEST";
export const REFRESH_TOKEN_SUCCESS = "REFRESH_TOKEN_SUCCESS";
export const REFRESH_TOKEN_FAILURE = "REFRESH_TOKEN_FAILURE";

export const SET_KEEP_LOGIN = "SET_KEEP_LOGIN";

export const CSO_LOGIN = "CSO_LOGIN";
export const CSO_LOGIN_REQUEST = "CSO_LOGIN_REQUEST";
export const CSO_LOGIN_SUCCESS = "CSO_LOGIN_SUCCESS";
export const CSO_LOGIN_FAILURE = "CSO_LOGIN_FAILURE";

export const CREATE_ACCOUNT = "CREATE_ACCOUNT";
export const CREATE_ACCOUNT_REQUEST = "CREATE_ACCOUNT_REQUEST";
export const CREATE_ACCOUNT_SUCCESS = "CREATE_ACCOUNT_SUCCESS";
export const CREATE_ACCOUNT_FAILURE = "CREATE_ACCOUNT_FAILURE";

export const IS_REGISTER_SUCCESS = "IS_REGISTER_SUCCESS";

export const REFRESH_INVITE_PAGE_SUCCESS = "REFRESH_INVITE_PAGE_SUCCESS ";

const cookieToken = getCookie(JWT_TOKEN);
const cookieNoOfTries = getCookie(NO_OF_TRIES);
const userCk = cookieToken ? jwtDecode(cookieToken) : null;

const initialState = {
  isAuthenticated: cookieToken ? true : false,
  noOfTries: cookieNoOfTries ? parseInt(cookieNoOfTries, 10) : 0,
  token: cookieToken,
  keepLogin: userCk && stringToBoolean(userCk.keep_login),
  errorMessage: ""
};

const setKeepLogin = value => {
  return {
    type: SET_KEEP_LOGIN,
    payload: { keepLogin: stringToBoolean(value) }
  };
};

const loadingLoginSelector = createLoadingSelector([LOGIN]);
export const login = (values, formId) => (dispatch, getState) => {
  const isFetching = loadingLoginSelector(getState());
  if (!values || isFetching) return Promise.resolve();
  let data = {
    userName: values.username,
    password: values.password,
    keepLogin: values.keepLogin,
    recaptcha: ""
  };
  return Promise.resolve(dispatch(startSubmit(formId)))
    .then(() => dispatch(getToken()))
    .then(() =>
      dispatch({
        types: [LOGIN_REQUEST, LOGIN_SUCCESS, LOGIN_FAILURE],
        callAPI: config =>
          apiClient.put(`MemberAccount/Authenticate`, data, config),
        payload: { data, formId, isCaptcha: true }
      })
    )
    .then(
      resp => {
        // const { token } = resp.payload;
        const token = resp.response.data.result.token;
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        return Promise.resolve()
          .then(() => {
            const user = jwtDecode(token);
            return dispatch(setKeepLogin(user.keep_login));
          })
          .then(() => {
            setCookie(JWT_TOKEN, token, 999);
            deleteCookie(NO_OF_TRIES);
            return Promise.resolve();
          })
          .then(() => {
            const currentLanguage = getActiveLanguage(getState().locale).code;
            // redirect to card page after success login
            return dispatch(push(`/dashboard`));
            return Promise.resolve();
          });
      },
      err => {
        increaseNoOfTries();
        // return Promise.reject(error);
        return Promise.all([
          dispatch(showMessageModal(`${err.error.response.data.error.message}`))
        ]);
      }
    );
};

const increaseNoOfTries = () => {
  // store no of tries into cookie to show captcha even after page refreshes
  let noOfTries = getCookie(NO_OF_TRIES);
  noOfTries = noOfTries ? parseInt(noOfTries, 10) : 0;
  //set captcha for 1 hours
  setCookie(NO_OF_TRIES, ++noOfTries, 1 / 24);
};

// no cso login for now
// #region cso
export const csoLoginMemberSelector = createLoadingSelector([CSO_LOGIN]);
export const csoLoginMember = token => (dispatch, getState) => {
  const isFetching = csoLoginMemberSelector(getState());
  if (!token || isFetching) return Promise.resolve();

  return dispatch({
    types: [CSO_LOGIN_REQUEST, CSO_LOGIN_SUCCESS, CSO_LOGIN_FAILURE],
    callAPI: config => apiClient.post("api/account/csoLogin", token, config),
    payload: { token }
  }).then(resp => {
    const { token, returnAction } = resp.payload;
    // store user details and jwt token in local storage to keep user logged in between page refreshes
    return Promise.resolve()
      .then(() => {
        const user = jwtDecode(token);
        return dispatch(setKeepLogin(user.keep_login));
      })
      .then(() => {
        setCookie(JWT_TOKEN, token, 999);
        deleteCookie(NO_OF_TRIES);
        return Promise.resolve();
      })
      .then(
        () => {
          const currentLanguage = getActiveLanguage(getState().locale).code;

          switch (returnAction) {
            case VERIFY_GOLD_CARD_ADDRESS:
              dispatch(
                push(`/${currentLanguage}/cards/verify-gold-card-address`)
              );
              break;

            default:
              dispatch(push(`/${currentLanguage}/account`));
              break;
          }

          return Promise.resolve();
        },
        error => {
          increaseNoOfTries();
          return Promise.reject(error);
        }
      );
  });
};
// #endregion cso

const loadingRefreshTokenSelector = createLoadingSelector([LOGIN]);
export const refreshToken = () => (dispatch, getState) => {
  const isFetching = loadingRefreshTokenSelector(getState());
  if (isFetching || !getState().auth.isAuthenticated) return Promise.resolve();

  return dispatch({
    types: [
      REFRESH_TOKEN_REQUEST,
      REFRESH_TOKEN_SUCCESS,
      REFRESH_TOKEN_FAILURE
    ],
    callAPI: config => apiClient.get(`MemberAccount/RefreshToken`, config)
  }).then(resp => {
    const { token } = resp.response.data.result;
    // store user details and jwt token in local storage to keep user logged in between page refreshes
    return Promise.resolve()
      .then(() => {
        const user = jwtDecode(token);
        return dispatch(setKeepLogin(user.keep_login));
      })
      .then(() => {
        setCookie(JWT_TOKEN, token, 999);
        return Promise.resolve();
      });
  });
};

const requestLogout = () => dispatch => {
  dispatch({
    type: LOGOUT_REQUEST,
    payload: {
      isAuthenticated: true,
      errorMessage: "",
      successMessage: ""
    }
  });

  return Promise.resolve();
};

const receiveLogout = () => (dispatch, getState) => {
  dispatch({
    type: LOGOUT_SUCCESS,
    payload: { isAuthenticated: false, errorMessage: "" }
  });
  const currentLanguage = getActiveLanguage(getState().locale).code;
  // redirect to card page after success login
  dispatch(push(`/sign-in`));
  return Promise.resolve();
};

export const logout = () => (dispatch, getState) => {
  if (!getState().auth.isAuthenticated) return Promise.resolve();

  return Promise.resolve()
    .then(() => dispatch(requestLogout()))
    .then(() => {
      //remove token from cookie
      deleteCookie(JWT_TOKEN);
      deleteCookie(NO_OF_TRIES);

      return Promise.resolve();
    })
    .then(() => dispatch(receiveLogout()));
};

export const submittingCreateAccountSelector = createLoadingSelector([
  CREATE_ACCOUNT
]);
export const createAccount = caModel => (dispatch, getState) => {
  const isFetching = submittingCreateAccountSelector(getState());
  if (!caModel || isFetching) return Promise.resolve();
  let mobile = caModel.mobileNo.split("-");
  // let countryCode = mobile[0].slice(1, mobile[0].length);
  let mobileNo = mobile[1];
  let caData = {
    firstName: caModel.firstName,
    lastName: caModel.lastName,
    gender: caModel.gender,
    dob: caModel.dob,
    referrerCode: caModel.referralCode,
    password: caModel.password,
    email: caModel.email,
    mobileNo: mobileNo,
    emailSubscription: true
  };

  return Promise.resolve(dispatch(startSubmit(signUpFormId)))
    .then(() => dispatch(getToken()))
    .then(() =>
      dispatch({
        types: [
          CREATE_ACCOUNT_REQUEST,
          CREATE_ACCOUNT_SUCCESS,
          CREATE_ACCOUNT_FAILURE
        ],
        callAPI: config =>
          apiClient.put("MemberAccount/Register", caData, config),
        payload: { caData, formId: signUpFormId }
      })
    )
    .then(
      resp => {
        const { token } = resp.response.data.result;
        if (resp) {
          const user = jwtDecode(token);
          setCookie(JWT_TOKEN, token, 999);
          dispatch(isRegister(true));
          return dispatch(push(`/dashboard`));
          // return dispatch(
          //   showMessageModal("Sign up success", () =>
          //     dispatch(push(`/dashboard`))
          //   )
          // );
        }
      },
      error => {
        // let errorMessage = error.error.response.data.error.message;
        if (error) {
          return dispatch(showMessageModal("Sign up failed.Please try again"));
        }
        return Promise.reject(error);
      }
    )
    .catch(err => {
      const status = err.error.response.status;
      if (status === 500) {
        return dispatch(
          showMessageModal(`${err.error.response.data.error.message}`)
        );
      }
      return dispatch(
        showMessageModal(`${err.error.response.data.errors[0].errorMessage}`)
      );
    });
};
export const isRegister = isRegister => dispatch => {
  dispatch({
    type: IS_REGISTER_SUCCESS,
    payload: { isRegister: isRegister, errorMessage: "" }
  });
  return Promise.resolve();
};

export const refreshInvitePage = isRefreshInvite => dispatch => {
  dispatch({
    type: REFRESH_INVITE_PAGE_SUCCESS,
    payload: { isRefreshInvite: isRefreshInvite, errorMessage: "" }
  });
  return Promise.resolve();
};

export default function reducer(state = initialState, action) {
  const { errorMessage, payload } = action;
  switch (action.type) {
    case SET_KEEP_LOGIN:
    case LOGOUT_REQUEST:
    case REFRESH_TOKEN_SUCCESS:
      return {
        ...state,
        ...payload
      };
    case LOGOUT_SUCCESS:
    case LOGIN_REQUEST:
    case CSO_LOGIN_REQUEST:
      return {
        ...state,
        token: "",
        isAuthenticated: false,
        keepLogin: false,
        errorMessage: ""
      };
    case LOGIN_SUCCESS:
    case CSO_LOGIN_SUCCESS:
      return {
        ...state,
        ...payload,
        isAuthenticated: true,
        noOfTries: 0
      };
    case LOGIN_FAILURE:
    case CSO_LOGIN_FAILURE:
      return {
        ...state,
        noOfTries: ++state.noOfTries,
        errorMessage
      };
    case REFRESH_TOKEN_FAILURE:
      return {
        ...state,
        errorMessage
      };
    case CREATE_ACCOUNT_REQUEST:
    case CREATE_ACCOUNT_FAILURE:
      return {
        ...state,
        isAuthenticated: false
      };
    case CREATE_ACCOUNT_SUCCESS:
      return {
        ...state,
        isAuthenticated: true
      };
    case IS_REGISTER_SUCCESS:
      return {
        ...state,
        isRegister: payload.isRegister
      };
    case REFRESH_INVITE_PAGE_SUCCESS:
      return {
        ...state,
        isRefreshInvite: payload.isRefreshInvite
      };
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/redux/modules/Auth.js